import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Carousel } from "pcln-carousel";
import { Box } from "pcln-design-system";
import { useContext, useEffect, useMemo, useState } from "react";
import { HotelFilterComponent } from "./HotelFilterComponent.js";
import analytics from "../analytics/analytics.js";
import { ChatbotDataContext } from "../context/ChatbotDataContext.js";
import { useRealTimeContext } from "../hooks/useRealTimeContext.js";
import HotelListingCard from "./HotelListingCard/HotelListingCard.js";
import LoadMore from "./HotelListingCard/components/LoadMore/LoadMore.js";
import { useSetHotelPinOnMapHook } from "./useSetHotelPinOnMapHook.js";
const sopqImages = [
    'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-3-2.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp',
    'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-3-3.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp',
    'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-4-1.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp',
    'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-3-1.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp',
    'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-2-1.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp',
    'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-4-3.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp',
    'https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-2-3.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp'
];
const getDefaultSopqRoomImage = (starRating)=>{
    const imageRating = Number.isNaN(Number(starRating)) ? 0 : Math.floor(Number(starRating));
    return imageRating ? `https://s1.pclncdn.com/design-assets/sopq/img-sopq-room-${imageRating}-1.jpg?width=250&crop=1:1&dpr=2&format=jpg&opto&auto=avif,webp` : sopqImages[0];
};
const transformAmenitiesText = (amenities)=>{
    if (amenities) {
        const amenityMap = {
            FINTRNT: 'Free Internet Access',
            FINTRAVL: 'Free Internet Available',
            FINTRPUB: 'Free Internet In Public Areas',
            FINTRRM: 'Free Internet In Room',
            'FREE WIFI': 'Free Internet In Room',
            PETALLOW: 'Pets Allowed',
            NSMKFAC: 'No Smoking Rooms/Facilities',
            BUSCNTR: 'Business Center',
            CASINO: 'Casino',
            FPRKING: 'Free Parking',
            BEDDING: 'Bedding',
            SPOOL: 'Swimming Pool',
            SPOOLIN: 'Indoor Swimming Pool',
            SPOOLOUT: 'Outdoor Swimming Pool',
            FITSPA: 'Fitness Center',
            SPA: 'Spa',
            FBRKFST: 'Free Breakfast',
            FBRKST: 'Free Breakfast',
            AIRSHUTTL: 'Airport Shuttle',
            FAIRSHUTTL: 'Free Airport Shuttle',
            HANDFAC: 'Accessible Rooms/Facilities',
            RESTRNT: 'Restaurant',
            BCHFRNT: 'Beachfront',
            FRIDGE: 'Fridge',
            MW: 'Microwave',
            TUB: 'Whirlpool',
            INCLUSIVE: 'All Inclusive',
            ADULTONLY: 'Adults Only',
            KITCHEN: 'Kitchen',
            KITCHENETTE: 'Kitchenette'
        };
        const firstTextMember = amenities.map((item)=>amenityMap[item]).join(', ');
        const secondTextMember = 'Fully Refundable Rates, Pay Later  Available';
        return [
            firstTextMember,
            secondTextMember
        ];
    }
    return '';
};
const transformListingProps = (hotel)=>{
    const { ratesSummary, totalReviewCount, starRating, images, hotelFeatures, overallGuestRating, location, name, displayStrikePrice, hotelType } = hotel ?? {};
    const imageSrc = 'SOPQ' === hotelType ? images?.[0]?.fastlyUrl ?? getDefaultSopqRoomImage(starRating) : images?.[0]?.fastlyUrl ?? '';
    return {
        imageSrc,
        amenitiesList: transformAmenitiesText(hotelFeatures?.highlightedAmenities),
        ratingsProps: {
            guestRating: totalReviewCount,
            hotelRating: starRating,
            overallRating: overallGuestRating
        },
        priceProps: {
            price: ratesSummary?.minPrice,
            totalPrice: ratesSummary?.grandTotal,
            grandTotalExcludingTax: ratesSummary?.grandTotalExcludingTax,
            grandTotalSavingsAmount: ratesSummary?.grandTotalSavingsAmount,
            grandTotalSavingsAmountExcludingTax: ratesSummary?.grandTotalSavingsAmountExcludingTax,
            grandTotalSavingsPctExcludingTax: ratesSummary?.grandTotalSavingsPctExcludingTax,
            grandTotalStrikeThroughPrice: ratesSummary?.grandTotalStrikeThroughPrice,
            grandTotalStrikeThroughPriceExcludingTax: ratesSummary?.grandTotalStrikeThroughPriceExcludingTax,
            currency: ratesSummary?.minCurrencyCodeSymbol,
            strikeThroughPrice: ratesSummary?.minStrikePrice,
            savingsPct: ratesSummary?.savingsPct,
            nightlyRateIncludingTaxesAndFees: ratesSummary.nightlyRateIncludingTaxesAndFees,
            nightlyRateExcludingTaxesAndFees: ratesSummary.nightlyRateExcludingTaxesAndFees,
            nightlyAllRoomsPriceExcludingTax: ratesSummary.nightlyAllRoomsPriceExcludingTax,
            nightlyAllRoomsStrikeThroughPriceExcludingTax: ratesSummary.nightlyAllRoomsStrikeThroughPriceExcludingTax,
            nightlyAllRoomsSavingsAmountExcludingTax: ratesSummary.nightlyAllRoomsSavingsAmountExcludingTax,
            nightlyInclusiveStrikeThroughPrice: ratesSummary.nightlyInclusiveStrikeThroughPrice,
            priceDisplayRegulation: ratesSummary?.priceDisplayRegulation,
            strikePriceExcludingTaxesAndFees: ratesSummary?.strikePriceExcludingTaxesAndFees,
            strikePriceIncludingTaxesAndFees: ratesSummary?.strikePriceIncludingTaxesAndFees,
            displayStrikePrice
        },
        headerText: {
            hotelName: name,
            neighborhoodName: location.neighborhoodName
        }
    };
};
const HotelDetailCardsMessage = (props)=>{
    useEffect(()=>{
        analytics.log({
            label: 'HotelSearchResult',
            message: 'HOTEL_CARDS_DISPLAY',
            ...props?.args
        });
    }, []);
    const { isShowMoreListingCards, isPricingVariant } = props;
    const { isMobile } = useContext(ChatbotDataContext);
    const highRatedHotels = props.searchResult?.hotels?.filter((hotel)=>hotel.overallGuestRating > 7.5);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [offset, setOffset] = useState(3);
    const [currentSlideOffset, setCurrentSlideOffset] = useState(0);
    const [displaySopqOnly, setDisplaySopqOnly] = useState(false);
    const { startRealTime } = useRealTimeContext();
    const hotelsLen = props.searchResult?.hotels?.length;
    const sortedHotels = props.searchResult?.hotels;
    const hasMoreHotels = offset < hotelsLen;
    const { hasSopqHotel, hasRtlHotel } = (props.searchResult?.hotels ?? []).reduce((flags, hotel)=>{
        if ('SOPQ' === hotel.hotelType) flags.hasSopqHotel = true;
        if ('SOPQ' !== hotel.hotelType) flags.hasRtlHotel = true;
        return flags;
    }, {
        hasSopqHotel: false,
        hasRtlHotel: false
    });
    const reviewConfiguration = props.searchResult?.reviewConfiguration;
    const displayResult = useMemo(()=>{
        if (isShowMoreListingCards) {
            const result = [
                ...sortedHotels?.slice(0, offset) ?? []
            ];
            result.push({
                type: hasMoreHotels ? 'loadMore' : 'endOfList'
            });
            return result;
        }
        {
            if (displaySopqOnly) return props.searchResult?.hotels?.filter((hotel)=>'SOPQ' === hotel.hotelType);
            let hotelsToDisplay;
            hotelsToDisplay = props.displayVectorSearchResult ? props.searchResult?.hotels : highRatedHotels?.length ? props.searchResult?.hotels?.filter((hotel)=>hotel.overallGuestRating > 7.5)?.slice(0, 3) : props.searchResult?.hotels?.slice(0, 3);
            return hotelsToDisplay;
        }
    }, [
        props,
        offset,
        hasMoreHotels,
        displaySopqOnly
    ]);
    function onLoadMore() {
        analytics.log({
            label: 'LoadMoreListingHotels',
            message: 'LOAD_MORE_HOTELS_CLICKED'
        });
        if (offset < props.searchResult?.hotels?.length) {
            setCurrentSlide(currentSlide + 3);
            setOffset(offset + 3);
            setCurrentSlideOffset(currentSlideOffset + 1);
        }
    }
    useSetHotelPinOnMapHook({
        displaySopqOnly,
        displayResult,
        args: props?.args
    });
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(Box, {
                maxWidth: startRealTime && isMobile ? '370px' : null,
                children: /*#__PURE__*/ jsx(Carousel, {
                    arrowPositions: "side",
                    showForwardBackBtns: true,
                    visibleSlides: 2,
                    onSlideChange: function() {},
                    overflowAllowancePxX: isMobile ? 16 : 32,
                    currentSlide: currentSlide,
                    displayArrowsMobile: isMobile,
                    children: displayResult?.map(function(hotel, index) {
                        if ('loadMore' === hotel.type && isShowMoreListingCards) return /*#__PURE__*/ jsx(Box, {
                            children: /*#__PURE__*/ jsx(LoadMore, {
                                onLoadMore: onLoadMore
                            })
                        }, "load_more_listing_card");
                        if ('endOfList' === hotel.type) return /*#__PURE__*/ jsx(Box, {}, 'end_of_list_listing_card');
                        return /*#__PURE__*/ jsx(Box, {
                            children: /*#__PURE__*/ jsx(HotelListingCard, {
                                listingProps: transformListingProps(hotel),
                                hotel: hotel,
                                args: props?.args,
                                isPricingVariant: isPricingVariant,
                                reviewConfiguration: reviewConfiguration,
                                submitMessages: props.submitMessages
                            })
                        }, String(hotel.hotelId));
                    })
                })
            }),
            startRealTime ? null : /*#__PURE__*/ jsx(HotelFilterComponent, {
                ...props,
                hasSopqHotel: hasSopqHotel,
                hasRtlHotel: hasRtlHotel,
                displaySopqOnly: displaySopqOnly,
                toggleOnlyExpressDealClick: function() {
                    analytics.log({
                        label: 'HotelSearchResult',
                        message: 'TOGGLE_EXPRESS_DEAL_RESULTS_BUTTON_CLICKED',
                        ...props?.args
                    });
                    setDisplaySopqOnly(!displaySopqOnly);
                }
            })
        ]
    });
};
export { HotelDetailCardsMessage, getDefaultSopqRoomImage, sopqImages, transformListingProps };
